import { useCallback, useEffect, useState } from "react";
import {
	useAlertContext,
	useClinicContext,
	useDoctorContext,
	useUserContext
} from "../../contexts";
import { AlertType, Doctor } from "../../models";
import { apiAccessRequest } from "../../constants";
import { BuildUrl } from "../../helpers";
import { useMsal } from "@azure/msal-react";

export const GetDoctors = () => {
	const { instance } = useMsal();
	const { account } = useUserContext();
	const { telehealthClinics, userClinics } = useClinicContext();
	const {
		refreshTelehealthDoctors,
		setRefreshTelehealthDoctors,
		setActiveTelehealthDoctors,
		setTelehealthDoctors,
		setFaceToFaceDoctors,
		refreshFaceToFaceDoctors,
		setRefreshFaceToFaceDoctors,
		faceToFaceDoctors,
		telehealthDoctors,
		setDoctorImages,
		refreshDoctorImages,
		setRefreshDoctorImages
	} = useDoctorContext();
	const { showAlert } = useAlertContext();
	const [doctorImagesHaveBeenRetrieved, setDoctorImagesHaveBeenRetrieved] =
		useState(false);

	const getTelehealthDoctors = useCallback(async () => {
		try {
			const requestUrl = BuildUrl("Doctor/search");
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				}),
				...{ "Content-Type": "application/json" }
			};
			const response = await fetch(requestUrl, {
				method: "POST",
				headers,
				body: JSON.stringify({
					clinicIds: telehealthClinics?.map((clinic) => clinic.amsId),
					excludeNotTakingTelehealth: true
				})
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			const contentLength = response.headers.get("Content-Length");
			if (response.status !== 204 && contentLength !== "0") {
				const result = await response.json();
				// console.info(
				// 	"GetDoctors getTelehealthDoctors",
				// 	result.sort(sortDoctor)
				// );
				setTelehealthDoctors(result.sort(sortTelehealthDoctor));
				setActiveTelehealthDoctors(
					result.filter(
						(doctor: Doctor) =>
							doctor.status &&
							doctor.takingPatientsTelehealth &&
							doctor.availableTelehealth
					)
				);
			}
		} catch (error) {
			console.info(error);
			showAlert({
				message: "Doctors could not be retrieved",
				alertType: AlertType.error
			});
			return;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		account,
		instance,
		setActiveTelehealthDoctors,
		setTelehealthDoctors,
		showAlert,
		telehealthClinics
	]);

	const sortTelehealthDoctor = useCallback((a: Doctor, b: Doctor) => {
		if (a.availableTelehealth && !b.availableTelehealth) {
			return -1;
		}
		if (!a.availableTelehealth && b.availableTelehealth) {
			return 1;
		}
		if (a.availableTelehealth === b.availableTelehealth) {
			return a.fullName < b.fullName ? -1 : 1;
		}
	}, []);

	const getFaceToFaceDoctors = useCallback(async () => {
		try {
			const requestUrl = BuildUrl("Doctor/search");
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				}),
				...{ "Content-Type": "application/json" }
			};
			const response = await fetch(requestUrl, {
				method: "POST",
				headers,
				body: JSON.stringify({
					clinicIds: userClinics?.map((clinic) => clinic.amsId)
				})
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			const contentLength = response.headers.get("Content-Length");
			if (response.status !== 204 && contentLength !== "0") {
				const result = await response.json();
				// console.info(
				// 	"GetDoctors getFaceToFaceDoctors",
				// 	result.sort(sortDoctor)
				// );
				setFaceToFaceDoctors(result.sort(sortFaceToFaceDoctor));
			}
		} catch (error) {
			console.info(error);
			showAlert({
				message: "Doctors could not be retrieved",
				alertType: AlertType.error
			});
			return;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [account, instance, setFaceToFaceDoctors, showAlert, userClinics]);

	const sortFaceToFaceDoctor = useCallback((a: Doctor, b: Doctor) => {
		return a.fullName < b.fullName ? -1 : 1;
	}, []);

	const getDoctorImages = useCallback(
		async (doctorIds: number[]) => {
			try {
				const requestUrl = BuildUrl("DoctorImages/GetDoctorImages");
				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});
				const headers = {
					...(tokenResponse && {
						Authorization: `Bearer ${tokenResponse.accessToken}`
					}),
					...{ "Content-Type": "application/json" }
				};
				const response = await fetch(requestUrl, {
					method: "POST",
					headers,
					body: JSON.stringify(doctorIds)
				});

				if (!response.ok) {
					throw new Error("Network response was not ok");
				}

				const contentLength = response.headers.get("Content-Length");
				if (response.status !== 204 && contentLength !== "0") {
					const result = await response.json();
					// console.info(
					// 	"GetDoctors getFaceToFaceDoctors",
					// 	result.sort(sortDoctor)
					// );
					setDoctorImages(result);
				}
				setDoctorImagesHaveBeenRetrieved(true);
				setRefreshDoctorImages(false);
			} catch (error) {
				console.info(error);
				showAlert({
					message: "Doctors images could not be retrieved",
					alertType: AlertType.error
				});
				return;
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[account, instance, setDoctorImages, setRefreshDoctorImages, showAlert]
	);

	useEffect(() => {
		if (telehealthClinics) {
			getTelehealthDoctors();
			setRefreshTelehealthDoctors(false);
			return;
		}
	}, [telehealthClinics, setRefreshTelehealthDoctors, getTelehealthDoctors]);

	useEffect(() => {
		if (refreshTelehealthDoctors) {
			console.info(
				"GetDoctors refreshTelehealthDoctors",
				refreshTelehealthDoctors
			);
			getTelehealthDoctors();
			setRefreshTelehealthDoctors(false);
		}
	}, [
		getTelehealthDoctors,
		refreshTelehealthDoctors,
		setRefreshTelehealthDoctors
	]);

	useEffect(() => {
		if (userClinics) {
			getFaceToFaceDoctors();
			setRefreshFaceToFaceDoctors(false);
			return;
		}
	}, [userClinics, setRefreshFaceToFaceDoctors, getFaceToFaceDoctors]);

	useEffect(() => {
		if (refreshFaceToFaceDoctors) {
			console.info(
				"GetDoctors refreshFaceToFaceDoctors",
				refreshFaceToFaceDoctors
			);
			getFaceToFaceDoctors();
			setRefreshFaceToFaceDoctors(false);
		}
	}, [
		getFaceToFaceDoctors,
		refreshFaceToFaceDoctors,
		setRefreshFaceToFaceDoctors
	]);

	useEffect(() => {
		if (
			faceToFaceDoctors &&
			faceToFaceDoctors.length > 0 &&
			telehealthDoctors &&
			telehealthDoctors.length > 0 &&
			(!doctorImagesHaveBeenRetrieved || refreshDoctorImages)
		) {
			const doctorIds = faceToFaceDoctors
				.map((doctor) => doctor.id)
				.concat(telehealthDoctors.map((doctor) => doctor.id));
			getDoctorImages(doctorIds);
		}
	}, [
		doctorImagesHaveBeenRetrieved,
		faceToFaceDoctors,
		getDoctorImages,
		refreshDoctorImages,
		telehealthDoctors
	]);

	return <></>;
};
